import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemButton,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { zohoAuthService } from '@/services/zohoAuth';
import { zohoProjectsApiService } from '@/services/zohoProjectsApi';

interface Portal {
  id: string;
  name: string;
  portal_name: string;
}

interface Project {
  id: string;
  name: string;
  description: string;
}

interface PortalSelectorProps {
  onProjectSelect?: (project: Project) => void;
  onPortalSelect?: (portal: Portal) => void;
}

export const PortalSelector: React.FC<PortalSelectorProps> = ({ onPortalSelect }) => {
  const [portals, setPortals] = useState<Portal[]>([]);
  const [selectedPortal, setSelectedPortal] = useState<Portal | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Récupérer les portails au chargement
  useEffect(() => {
    loadPortals();
  }, []);

  const loadPortals = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await zohoAuthService.getValidToken();
      if (!token) {
        setError('Token d\'accès invalide');
        return;
      }

      const portalsList = await zohoAuthService.getPortals(token);
      
      setPortals(portalsList);
      
      if (portalsList.length === 0) {
        setError('Aucun portail trouvé. Vérifiez que vous avez le scope ZohoProjects.portals.ALL');
      } else {

      }
    } catch (err) {
      setError('Erreur lors du chargement des portails');
      console.error('Erreur loadPortals:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async (portal: Portal) => {
    // Éviter le rechargement si les projets sont déjà chargés pour ce portail
    if (selectedPortal && selectedPortal.id === portal.id && projects.length > 0) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await zohoAuthService.getValidToken();
      if (!token) {
        setError('Token d\'accès invalide');
        return;
      }

      // Utiliser la nouvelle méthode du service zohoProjectsApi
      const projectsList = await zohoProjectsApiService.getProjects(token, portal.id);
      
      setProjects(projectsList);
      setSelectedPortal(portal);
      
      if (projectsList.length === 0) {
        setError('Aucun projet trouvé dans ce portail');
      } else {

      }
    } catch (err) {
      setError('Erreur lors du chargement des projets');
      console.error('Erreur loadProjects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePortalSelect = (portal: Portal) => {
    // Ne pas recharger si le même portail est déjà sélectionné
    if (selectedPortal && selectedPortal.id === portal.id) {
      return;
    }
    
    // Charger les projets en arrière-plan sans bloquer l'interface
    loadProjects(portal);
    
    // Notifier immédiatement la sélection du portail
    if (onPortalSelect) {
      onPortalSelect(portal);
    }
  };

  // const handleProjectSelect = (project: Project) => {
  //   if (onProjectSelect) {
  //     onProjectSelect(project);
  //   }
  // };

  if (loading && portals.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Sélection du Portail
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Liste des portails */}
        {portals.length > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Portails disponibles ({portals.length})
            </Typography>
            <List>
              {portals.map((portal) => (
                <ListItem key={portal.id} disablePadding>
                  <ListItemButton 
                    onClick={() => handlePortalSelect(portal)}
                    selected={selectedPortal ? selectedPortal.id === portal.id : false}
                    disabled={loading}
                  >
                    <ListItemText 
                      primary={portal.name || portal.portal_name}
                      secondary={`ID: ${portal.id}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {/* Indicateur de chargement des projets */}
        {selectedPortal && loading && (
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Chargement des projets...
            </Typography>
          </Box>
        )}

        {/* Message si aucun portail */}
        {portals.length === 0 && !loading && !error && (
          <Alert severity="info">
            Aucun portail trouvé. Vérifiez vos permissions Zoho Projects.
          </Alert>
        )}

        {/* Bouton de rafraîchissement */}
        <Box mt={2}>
          <Button 
            variant="outlined" 
            onClick={loadPortals}
            disabled={loading}
          >
            Rafraîchir les portails
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}; 